import React, { useEffect, useState, useRef } from 'react';
import { TourTooltip } from './TourTooltip';
import { TourStep as TourStepType } from '@/services/tourService';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface TourStepProps {
  step: TourStepType;
  title: string;
  description: string;
  actionText: string;
  emphasize?: boolean;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onClose: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isLastStep: boolean;
  isActive: boolean;
}

interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourStep({
  step,
  title,
  description,
  actionText,
  emphasize = false,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onClose,
  canGoBack,
  canGoForward,
  isLastStep,
  isActive,
}: TourStepProps) {
  const [elementPosition, setElementPosition] = useState<ElementPosition | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(
    'bottom'
  );
  const targetRef = useRef<HTMLElement | null>(null);

  // Get target element selector based on step
  const getTargetSelector = (stepName: TourStepType): string => {
    switch (stepName) {
      case 'organizationSettings':
        return '[data-tour-target="organization-settings"]';
      case 'productsTable':
        return '[data-tour-target="products-table"]';
      case 'importPage':
        return '[data-tour-target="import-page"]';
      case 'documentation':
        return '[data-tour-target="documentation"]';
      case 'complete':
        return '[data-tour-target="tour-complete"]';
      default:
        return 'body';
    }
  };

  // Find and position target element
  useEffect(() => {
    if (!isActive) return;

    const selector = getTargetSelector(step);

    const findTargetElement = () => {
      const element = document.querySelector(selector) as HTMLElement;

      if (element) {
        targetRef.current = element;
        const rect = element.getBoundingClientRect();

        setElementPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });

        // Add highlight class to target element
        element.classList.add('tour-highlight');

        // Scroll element into view
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        return true;
      }
      return false;
    };

    // Try to find element immediately
    if (!findTargetElement()) {
      // If not found, try again with increased delays
      const timer1 = setTimeout(() => {
        if (!findTargetElement()) {
          const timer2 = setTimeout(() => {
            if (!findTargetElement()) {
              // Final fallback - show tooltip in center of screen
              setElementPosition({
                top: window.innerHeight / 2 - 100,
                left: window.innerWidth / 2 - 200,
                width: 400,
                height: 200,
              });
              // Element not found, but we'll show the tooltip anyway
            }
          }, 1000);
          return () => clearTimeout(timer2);
        }
      }, 500);

      return () => clearTimeout(timer1);
    }
  }, [step, isActive]);

  // Calculate tooltip position based on target element position
  useEffect(() => {
    if (!elementPosition) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 384; // max-w-sm = 384px
    const tooltipHeight = 300; // estimated height

    const { top, left, width, height } = elementPosition;

    // Determine best position for tooltip
    const spaceBelow = viewportHeight - (top + height);
    const spaceAbove = top;
    const spaceRight = viewportWidth - (left + width);
    const spaceLeft = left;

    if (spaceBelow >= tooltipHeight) {
      setTooltipPosition('bottom');
    } else if (spaceAbove >= tooltipHeight) {
      setTooltipPosition('top');
    } else if (spaceRight >= tooltipWidth) {
      setTooltipPosition('right');
    } else if (spaceLeft >= tooltipWidth) {
      setTooltipPosition('left');
    } else {
      setTooltipPosition('top'); // fallback to top
    }
  }, [elementPosition]);

  // Calculate tooltip position styles
  const getTooltipStyles = (): React.CSSProperties => {
    if (!elementPosition) return {};

    const { top, left, width, height } = elementPosition;

    switch (tooltipPosition) {
      case 'bottom':
        return {
          top: top + height + 16,
          left: left + width / 2 - 192, // center relative to target (384px / 2 = 192px)
          transform: 'translateX(0)',
        };
      case 'top':
        return {
          top: Math.max(16, top - 316), // clamp to viewport top
          left: left + width / 2 - 192,
          transform: 'translateX(0)',
        };
      case 'right':
        return {
          top: top + height / 2 - 150, // center vertically
          left: left + width + 16,
          transform: 'translateY(0)',
        };
      case 'left':
        return {
          top: top + height / 2 - 150,
          left: left - 400, // tooltip width + gap
          transform: 'translateY(0)',
        };
      default:
        return {};
    }
  };

  // Cleanup function
  useEffect(() => {
    return () => {
      if (targetRef.current) {
        targetRef.current.classList.remove('tour-highlight');
      }
    };
  }, []);

  // Don't render if not active
  if (!isActive) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-black/50"
          onClick={onClose}
        />
      </AnimatePresence>

      {/* Target element highlight */}
      {elementPosition && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="z-45 pointer-events-none fixed"
          style={{
            top: elementPosition.top - 4,
            left: elementPosition.left - 4,
            width: elementPosition.width + 8,
            height: elementPosition.height + 8,
          }}
        >
          <div className="size-full rounded-lg bg-primary/5 ring-2 ring-primary ring-opacity-75" />
        </motion.div>
      )}

      {/* Tooltip */}
      {elementPosition && (
        <TourTooltip
          title={title}
          description={description}
          actionText={actionText}
          emphasize={emphasize}
          currentStep={currentStep}
          totalSteps={totalSteps}
          onNext={onNext}
          onPrevious={onPrevious}
          onSkip={onSkip}
          onClose={onClose}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          isLastStep={isLastStep}
          position={tooltipPosition}
          className={cn('pointer-events-auto')}
          style={getTooltipStyles()}
        />
      )}
    </>
  );
}
